import { useMemo, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Bookmark, Check, ChevronDown, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import type { TSavedFilter, TSavedFilterValues } from "@/zapi-frontend/shared/types/savedFilters";
import {
  GET_SAVED_FILTERS_KEY,
  useDeleteSavedFilter,
  useSavedFilters,
  useUpdateSavedFilter
} from "@/zapi-frontend/shared/services/useSavedFilters";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { showUndoToast } from "@/zapi-frontend/shared/ui/UndoToast/UndoToast";
import { findMatchingSavedFilter } from "../../lib/matchSavedFilter";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { NamePromptModal } from "./_NamePromptModal";
import { useTranslation } from "react-i18next";

const UNDO_TOAST_DURATION_MS = 5000;

type TProps = {
  activeFilters: TSavedFilterValues;
  onApply: (filters: TSavedFilterValues) => void;
};

function extractErrorMessage(err: unknown): string | null {
  if (err instanceof AxiosError) {
    return err.response?.data?.message ?? err.message ?? null;
  }
  if (err instanceof Error) return err.message;
  return null;
}

export function SavedFiltersControl({ activeFilters, onApply }: TProps) {
  const { t } = useTranslation("projects");
  const { data: savedFilters = [], isLoading } = useSavedFilters();
  const updateMutation = useUpdateSavedFilter();
  const deleteMutation = useDeleteSavedFilter();

  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganizationStore();
  const savedFiltersKey = [GET_SAVED_FILTERS_KEY, currentOrganizationId];

  const [open, setOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<TSavedFilter | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);

  const matched = useMemo(
    () => findMatchingSavedFilter(savedFilters, activeFilters),
    [savedFilters, activeFilters]
  );

  const handleApply = (filter: TSavedFilter) => {
    onApply(filter.filters);
    setOpen(false);
  };

  const handleRename = async (name: string) => {
    if (!renameTarget) return;
    setRenameError(null);
    try {
      await updateMutation.mutateAsync({ id: renameTarget.id, name });
      toast.success(t("savedFilters.renamed"));
      setRenameTarget(null);
    } catch (err) {
      setRenameError(extractErrorMessage(err) ?? t("savedFilters.renameFailed"));
    }
  };

  const removeSavedFilterFromCache = (target: TSavedFilter) => {
    queryClient.setQueryData<TSavedFilter[]>(savedFiltersKey, (current) =>
      (current ?? []).filter((f) => f.id !== target.id)
    );
  };

  const restoreSavedFilterToCache = (target: TSavedFilter) => {
    queryClient.setQueryData<TSavedFilter[]>(savedFiltersKey, (current) => {
      const without = (current ?? []).filter((f) => f.id !== target.id);
      return [...without, target].sort(
        (a, b) => a.name.localeCompare(b.name) || a.id - b.id
      );
    });
  };

  const handleDelete = (target: TSavedFilter) => {
    removeSavedFilterFromCache(target);
    let cancelled = false;
    const commitTimer = window.setTimeout(() => {
      if (cancelled) return;
      deleteMutation.mutate(target.id, {
        onError: (err) => {
          restoreSavedFilterToCache(target);
          toast.error(extractErrorMessage(err) ?? t("savedFilters.deleteFailed"));
        }
      });
    }, UNDO_TOAST_DURATION_MS);
    showUndoToast({
      message: t("savedFilters.deleted", { name: target.name }),
      duration: UNDO_TOAST_DURATION_MS,
      onUndo: () => {
        cancelled = true;
        window.clearTimeout(commitTimer);
        restoreSavedFilterToCache(target);
      }
    });
  };

  const buttonLabel = matched ? matched.name : t("savedFilters.heading");

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <AppButton
            type="button"
            appearance="outlined"
            color="zinc150"
            data-testid="saved-filters-trigger"
            className={cn(
              "h-8 w-full max-w-[264px] shrink-0 gap-1 px-3 text-[14px] font-medium !text-innoveraZinc-900 shadow-none [&_svg]:!text-innoveraZinc-900 sm:w-auto",
              matched && "border-zinc-900"
            )}
          >
            <Bookmark className="h-4 w-4 shrink-0 text-zinc-600" strokeWidth={1.5} />
            <span className="truncate max-w-[160px]">{buttonLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-500" strokeWidth={1.5} />
          </AppButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-60 rounded-lg border-0 bg-white p-1 shadow-md ring-1 ring-inset ring-zinc-100"
        >
          <div className="flex h-8 items-center px-2 text-[14px] font-medium leading-[13px] text-zinc-500">
            {t("savedFilters.heading")}
          </div>
          <div className="mt-1 max-h-[320px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center px-2 py-6">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-500" />
              </div>
            ) : savedFilters.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-2 py-3 text-center">
                <Bookmark className="h-6 w-6 text-zinc-300" strokeWidth={1.5} />
                <div className="flex flex-col gap-2">
                  <p className="text-[14px] font-medium leading-[10px] text-zinc-900">
                    {t("savedFilters.emptyTitle")}
                  </p>
                  <FigmaTypography variant="body-3" className="text-zinc-500">
                    {t("savedFilters.emptyBody")}
                  </FigmaTypography>
                </div>
              </div>
            ) : (
              savedFilters.map((filter) => (
                <SavedFilterRow
                  key={filter.id}
                  filter={filter}
                  isMatched={matched?.id === filter.id}
                  onApply={() => handleApply(filter)}
                  onRename={() => {
                    setRenameError(null);
                    setRenameTarget(filter);
                    setOpen(false);
                  }}
                  onDelete={() => {
                    setOpen(false);
                    handleDelete(filter);
                  }}
                />
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      <NamePromptModal
        isOpen={renameTarget !== null}
        title={t("savedFilters.renameTitle")}
        confirmText={t("savedFilters.save")}
        initialName={renameTarget?.name ?? ""}
        isSubmitting={updateMutation.isPending}
        errorMessage={renameError}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleRename}
      />
    </>
  );
}

type TRowProps = {
  filter: TSavedFilter;
  isMatched: boolean;
  onApply: () => void;
  onRename: () => void;
  onDelete: () => void;
};

function SavedFilterRow({ filter, isMatched, onApply, onRename, onDelete }: TRowProps) {
  const { t } = useTranslation("projects");
  return (
    <div
      className="group flex items-center gap-1 rounded-md py-1.5 pl-2 pr-1 hover:bg-zinc-50"
      data-testid="saved-filter-row"
      data-filter-id={filter.id}
    >
      <button
        type="button"
        onClick={onApply}
        className="flex min-w-0 flex-1 items-center gap-2 text-left text-[14px] text-zinc-900"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
          {isMatched ? <Check className="h-4 w-4 text-zinc-900" strokeWidth={2} /> : null}
        </span>
        <span className="min-w-0 flex-1 truncate" title={filter.name}>
          {filter.name}
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("savedFilters.more")}
            className="rounded-md p-1 text-zinc-500 opacity-0 hover:bg-zinc-100 hover:text-zinc-700 focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          >
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={4} className="w-40 p-1">
          <DropdownMenuItem onSelect={onRename} className="gap-2 text-[14px] text-zinc-900">
            <Pencil className="h-4 w-4 text-zinc-700" strokeWidth={1.5} />
            {t("savedFilters.rename")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={onDelete}
            className="gap-2 text-[14px] text-[#d93d3d] focus:text-[#d93d3d]"
          >
            <Trash2 className="h-4 w-4 text-[#d93d3d]" strokeWidth={1.5} />
            {t("savedFilters.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
